/**
 * services/TradePromotionService.ts
 * Atomic promotion boundary: TradeSetupDraft → Trade + TradeExecution + first TradeExecutionEvent
 *
 * Orchestrates the ten-step promotion sequence inside a single Dexie 'rw' transaction,
 * converting a live-captured draft into a permanent Trade record with its first
 * execution event and (if applicable) balance history entry.
 *
 * §4.1–4.3 Promotion contract:
 *   - Pre-flight validation before transaction opens
 *   - Idempotency by operationId (step 2 inside transaction)
 *   - ONE Dexie transaction for all ten write steps
 *   - Draft tombstone + NoteDraft → Note remap in the same transaction
 *
 * @see TradeService.create — Trade upsert (step 3)
 * @see TradeExecutionService.createForPromotion — TradeExecution create (step 4)
 * @see TradeExecutionEventService.appendEvent — event append (step 5)
 * @see TradeExecutionService.updateProjectionFromEvent — projection update (step 6)
 * @see BalanceHistoryService.addExecutionTransaction — balance row (step 7)
 */

import { db } from '@/lib/db';
import { generateUUID } from '@/utils/uuid';
import { getOrCreateDeviceId } from '@/utils/deviceDetection';
import { checkPromotionReadiness } from '@/utils/promotionReadiness';
import { markAsInstrumentedWriteContext } from './sync/SyncImportContext';
import { getSyncOperationService } from './sync/SyncOperationService';
import { buildSetOps, buildDiffOps } from './sync/OperationBuilder';
import { tradeService } from './TradeService';
import { tradeExecutionService } from './TradeExecutionService';
import { tradeExecutionEventService } from './TradeExecutionEventService';
import { balanceHistoryService } from './BalanceHistoryService';
import type { TradeSetupDraft } from '@/types/transient/TradeDraft';
import type { BalanceHistory } from '@/types/schema/BalanceHistory';
import type { Note } from '@/types/schema/Note';
import type { SessionDraft } from '@/types/transient/SessionDraft';
import type { InstrumentSnapshot } from '@/types/shared/InstrumentSnapshot';
import type { RiskSnapshot } from '@/types/shared/RiskSnapshots';

// ── Public Types ───────────────────────────────────────────────────────────────

export interface PromoteInput {
  draft: TradeSetupDraft;
  /** ID of the live session in which the execution event is being recorded */
  sessionId: string;
  /** Caller-supplied idempotency key (e.g. uuid()) */
  operationId: string;
  eventType: 'entry-fill' | 'order-placed';
  // Sparse payload — only fields relevant to eventType:
  direction?: 'long' | 'short';
  quantity?: number;
  price?: number;
  occurredAt: string;
  /** Order type — for order-placed events (limit and stop only) */
  orderType?: 'limit' | 'stop';
  // Resolved by caller:
  accountId: string;
  instrumentSnapshot: InstrumentSnapshot;
  riskSnapshot?: RiskSnapshot;
  /** Required only for entry-fill — financial-impact event */
  balanceRow?: {
    amount: number;
    transactionType: BalanceHistory['transactionType'];
    description?: string;
    amountPrecision?: number;
  };
}

export interface PromoteResult {
  tradeId: string;
  tradeExecutionId: string;
  tradeExecutionEventId: string;
  /** true when operationId was already used — idempotent return */
  wasAlreadyPromoted: boolean;
}

// ── Service ────────────────────────────────────────────────────────────────────

class TradePromotionService {
  /**
   * Promote a TradeSetupDraft to Trade + TradeExecution + first TradeExecutionEvent.
   *
   * All write steps (3–10) execute inside a single Dexie 'rw' transaction.
   * Pre-flight validation (step 1) runs before the transaction opens.
   * Idempotent by operationId (step 2).
   *
   * @throws if artifactKind !== 'trade', draft is not promotion-ready (missing planning fields), accountId is missing, or operationId is missing
   */
  async promote(input: PromoteInput): Promise<PromoteResult> {
    const { draft, sessionId, operationId, eventType, occurredAt, accountId, instrumentSnapshot } =
      input;

    // ── Step 1: Pre-flight validation (before transaction) ─────────────────
    if (draft.artifactKind !== 'trade') {
      throw new Error(
        `[TradePromotionService] Expected artifactKind='trade', got '${String(draft.artifactKind)}'`
      );
    }
    const readiness = checkPromotionReadiness(draft);
    if (!readiness.isReady) {
      throw new Error(
        `[TradePromotionService] Draft is not ready for promotion. Missing fields: ${readiness.missingFields.join(', ')}`
      );
    }
    if (!draft.accountId) {
      throw new Error('[TradePromotionService] draft.accountId is required');
    }
    if (draft.accountId !== input.accountId) {
      throw new Error(
        `[TradePromotionService] input.accountId '${input.accountId}' does not match draft.accountId '${draft.accountId}'`
      );
    }
    if (!operationId) {
      throw new Error('[TradePromotionService] operationId is required');
    }
    // F-02 (PR62): sessionDraftId required — Step 9 tombstone writes to this session.
    // An empty string would silently skip tombstone and violate the §4.1–4.3 contract.
    if (!draft.sessionDraftId) {
      throw new Error('[TradePromotionService] draft.sessionDraftId is required for promotion');
    }
    if (!instrumentSnapshot) {
      throw new Error('[TradePromotionService] instrumentSnapshot is required');
    }
    if (!accountId) {
      throw new Error('[TradePromotionService] accountId is required');
    }
    if (eventType === 'entry-fill') {
      if (input.direction === undefined) {
        throw new Error('[TradePromotionService] entry-fill requires direction');
      }
      if (input.quantity === undefined || input.quantity <= 0) {
        throw new Error('[TradePromotionService] entry-fill requires quantity > 0');
      }
      if (input.price === undefined) {
        throw new Error('[TradePromotionService] entry-fill requires price');
      }
    }
    if (eventType === 'order-placed') {
      if (input.direction === undefined) {
        throw new Error('[TradePromotionService] order-placed requires direction');
      }
      if (input.quantity === undefined || input.quantity <= 0) {
        throw new Error('[TradePromotionService] order-placed requires quantity > 0');
      }
      if (input.price === undefined || input.price <= 0) {
        throw new Error('[TradePromotionService] order-placed requires price > 0');
      }
      if (input.orderType === undefined) {
        throw new Error('[TradePromotionService] order-placed requires orderType');
      }
    }

    let result: PromoteResult | undefined;

    await db.transaction(
      'rw',
      [
        db.trades,
        db.trade_executions,
        db.trade_execution_events,
        db.balance_histories,
        db.notes,
        db.note_drafts,
        db.session_drafts,
        db.sync_operations,
        db.sync_state,
      ],
      async () => {
        markAsInstrumentedWriteContext();

        // ── Step 2: Idempotency check ──────────────────────────────────────
        const existingEvent = await db.trade_execution_events
          .where('operationId')
          .equals(operationId)
          .first();

        if (existingEvent) {
          result = {
            tradeId: existingEvent.tradeId,
            tradeExecutionId: existingEvent.tradeExecutionId,
            tradeExecutionEventId: existingEvent.id,
            wasAlreadyPromoted: true,
          };
          return;
        }

        const now = new Date().toISOString();
        const deviceId = getOrCreateDeviceId();

        // ── Step 3: Trade upsert (idempotent by rootDraftId) ──────────────
        const trade = await tradeService.create({
          sourceDraftId: draft.id,
          rootDraftId: draft.rootDraftId,
          ...(sessionId ? { sessionId } : {}),
          ...(draft.originSessionDraftId ? { setupSessionId: draft.originSessionDraftId } : {}),
          ...(draft.phase1?.strategySnapshot
            ? { strategySnapshot: draft.phase1.strategySnapshot }
            : {}),
          ...(draft.phase1?.playbookSnapshot
            ? { playbookSnapshot: draft.phase1.playbookSnapshot }
            : {}),
          ...(draft.phase1?.criteriaSnapshot
            ? { criteriaSnapshot: draft.phase1.criteriaSnapshot }
            : {}),
          ...(draft.phase1?.confluenceSnapshot
            ? { confluenceSnapshot: draft.phase1.confluenceSnapshot }
            : {}),
          ...(draft.phase1?.entryModelSnapshot
            ? { entryModelSnapshot: draft.phase1.entryModelSnapshot }
            : {}),
          ...(draft.phase1?.images ? { images: draft.phase1.images } : {}),
        });

        // ── Step 4: TradeExecution create (idempotent by tradeId) ─────────
        const tradeExecution = await tradeExecutionService.createForPromotion({
          tradeId: trade.id,
          sourceDraftId: draft.id,
          accountId,
          openedSessionId: sessionId,
          instrumentSnapshot,
          ...(input.riskSnapshot ? { riskSnapshot: input.riskSnapshot } : {}),
        });

        // ── Step 4b: Re-promote guard ──────────────────────────────────────
        // Steps 3+4 are individually idempotent, so a second promote() call
        // with a DIFFERENT operationId would return the existing Trade and
        // TradeExecution but then write a duplicate event in step 5.
        // Guard: if the execution already has events, the promotion completed
        // — return early with wasAlreadyPromoted regardless of operationId.
        const existingEvents = await db.trade_execution_events
          .where('tradeExecutionId')
          .equals(tradeExecution.id)
          .filter(e => !e.deletedAt)
          .toArray();

        const firstExistingEvent = existingEvents
          .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
          .at(0);

        if (firstExistingEvent) {
          result = {
            tradeId: trade.id,
            tradeExecutionId: tradeExecution.id,
            tradeExecutionEventId: firstExistingEvent.id,
            wasAlreadyPromoted: true,
          };
          return;
        }

        // ── Step 5: Event append (idempotent by operationId) ──────────────
        const event = await tradeExecutionEventService.appendEvent({
          tradeExecutionId: tradeExecution.id,
          tradeId: trade.id,
          sessionId,
          eventType,
          operationId,
          occurredAt,
          ...(input.direction !== undefined ? { direction: input.direction } : {}),
          ...(input.quantity !== undefined ? { quantity: input.quantity } : {}),
          ...(input.price !== undefined ? { price: input.price } : {}),
          ...(input.orderType !== undefined ? { orderType: input.orderType } : {}),
        });

        // ── Step 6: Projection update ──────────────────────────────────────
        await tradeExecutionService.updateProjectionFromEvent(tradeExecution.id, event);

        // ── Step 7: Balance row (entry-fill only) ─────────────────────────
        if (eventType === 'entry-fill' && input.balanceRow) {
          await balanceHistoryService.addExecutionTransaction({
            accountId,
            timestamp: occurredAt,
            transactionType: input.balanceRow.transactionType,
            amount: input.balanceRow.amount,
            sourceExecutionEventId: event.id,
            tradeExecutionId: tradeExecution.id,
            ...(input.balanceRow.description
              ? { description: input.balanceRow.description }
              : {}),
            ...(input.balanceRow.amountPrecision !== undefined
              ? { amountPrecision: input.balanceRow.amountPrecision }
              : {}),
          });
        }

        // ── Step 8: NoteDraft → Note remap ────────────────────────────────
        const noteDrafts = await db.note_drafts
          .where('[noteType+ownerId]')
          .equals(['trade', draft.id])
          .toArray();

        const activeDrafts = noteDrafts.filter(nd => !nd.deletedAt);

        for (const noteDraft of activeDrafts) {
          const noteId = generateUUID();
          const note: Note = {
            id: noteId,
            createdAt: now,
            updatedAt: now,
            noteType: 'trade',
            ownerId: trade.id,
            timestamp: noteDraft.timestamp,
            text: noteDraft.text,
            captureContext: noteDraft.captureContext,
            ...(noteDraft.category ? { category: noteDraft.category } : {}),
            ...(noteDraft.tradePhase ? { tradePhase: noteDraft.tradePhase } : {}),
            ...(noteDraft.sessionIds ? { sessionIds: noteDraft.sessionIds } : {}),
            ...(noteDraft.tradeIds ? { tradeIds: noteDraft.tradeIds } : {}),
            ...(noteDraft.objectiveIds ? { objectiveIds: noteDraft.objectiveIds } : {}),
            ...(noteDraft.eventIds ? { eventIds: noteDraft.eventIds } : {}),
            ...(noteDraft.instrumentIds ? { instrumentIds: noteDraft.instrumentIds } : {}),
            ...(noteDraft.slippageRisk ? { slippageRisk: noteDraft.slippageRisk } : {}),
          };
          await db.notes.add(note);
          const noteOps = buildSetOps('notes', noteId, note as unknown as Record<string, unknown>);
          if (noteOps.length > 0) {
            await getSyncOperationService().logBatch(noteOps);
          }
        }

        // ── Step 9: Draft tombstone ────────────────────────────────────────
        const sessionDraft = await db.session_drafts.get(draft.sessionDraftId);
        if (sessionDraft?.trades) {
          const draftExists = sessionDraft.trades.some(t => t.id === draft.id);
          if (draftExists) {
            const updatedTrades = sessionDraft.trades.map(t =>
              t.id === draft.id
                ? { ...t, deletedAt: now, deletedByDevice: deviceId, updatedAt: now }
                : t
            );
            const candidateAfter = { ...sessionDraft, trades: updatedTrades };
            const sessionOps = buildDiffOps(
              'session_drafts',
              sessionDraft.id,
              sessionDraft as unknown as Record<string, unknown>,
              candidateAfter as unknown as Record<string, unknown>
            );
            if (sessionOps.length > 0) {
              sessionOps.push({
                table: 'session_drafts',
                recordId: sessionDraft.id,
                op: 'set',
                field: 'metadata.updatedAt',
                value: now,
              });
              const finalSessionDraft: SessionDraft = {
                ...candidateAfter,
                metadata: { ...candidateAfter.metadata, updatedAt: now },
              };
              await db.session_drafts.put(finalSessionDraft);
              await getSyncOperationService().logBatch(sessionOps);
            }
          }
        }

        // ── Step 10: NoteDraft tombstones ─────────────────────────────────
        for (const noteDraft of activeDrafts) {
          await db.note_drafts.update(
            noteDraft.id,
            {
              deletedAt: now,
              deletedByDevice: deviceId,
              updatedAt: now,
            } as unknown as Parameters<typeof db.note_drafts.update>[1]
          );
          await getSyncOperationService().logOperation({
            table: 'note_drafts',
            recordId: noteDraft.id,
            op: 'delete',
          });
        }

        result = {
          tradeId: trade.id,
          tradeExecutionId: tradeExecution.id,
          tradeExecutionEventId: event.id,
          wasAlreadyPromoted: false,
        };
      }
    );

    if (!result) {
      throw new Error('[TradePromotionService] Promote transaction completed without a result');
    }

    return result;
  }
}

export const tradePromotionService = new TradePromotionService();
